/* TGM cart rewards (2026-10-03)
   Keeps the gift lines of the cart in step with the number of watches and repaints the rewards
   bar. Stand-alone on purpose: main.js is obfuscated, so nothing here depends on its internals.

   Rules (read from the JSON the Liquid snippet prints):
     kit    quantity = number of watches
     strap  1 when watches >= tier 2, else 0
     travel 1 when watches >= tier 3, else 0
   A gift is any line with the hidden property _tgm_gift. Lines without it are never touched, so
   configurator lines keep their Kickflip properties.

   Sync runs on load, when the drawer opens, after every cart change and right before checkout.
   A lock plus a "pending" flag stop re-entrancy; a debounce absorbs fast +/- clicks; the DOM
   swap after a sync is done with the observer muted so it cannot trigger another sync. */
(function () {
  'use strict';
  if (window.TGMRewards) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SECTIONS_DRAWER = 'cart-drawer';
  var STORAGE_KEY = 'tgm_rewards_strap_variant';
  var DEBOUNCE_MS = 180;

  var cfg = null;
  var state = {
    syncing: false,
    pending: false,
    timer: null,
    muted: false,
    ready: false,
    lastSignature: ''
  };

  /* ---------- helpers ---------- */
  function log() {
    if (window.TGMRewardsDebug && window.console) console.log.apply(console, ['[tgm-rewards]'].concat([].slice.call(arguments)));
  }
  function readConfig() {
    var el = document.querySelector('[data-tgm-rewards-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function storageGet() {
    try { return window.sessionStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
  }
  function storageSet(value) {
    try { window.sessionStorage.setItem(STORAGE_KEY, String(value)); } catch (e) { /* private mode */ }
  }
  function request(path, body) {
    return fetch(ROOT.replace(/\/$/, '') + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) {
      if (!r.ok) throw new Error(path + ' ' + r.status);
      return r.json();
    });
  }
  // One retry, then give up quietly: a failed gift write must never block the cart.
  function requestRetry(path, body) {
    return request(path, body).catch(function () { return request(path, body); });
  }
  function isWatch(item) {
    var type = (item.product_type || '').trim().toLowerCase();
    return cfg.watchTypes.some(function (t) { return t.trim().toLowerCase() === type; });
  }
  function giftRole(item) {
    return item.properties && item.properties[cfg.propertyKey] ? String(item.properties[cfg.propertyKey]) : '';
  }
  function strapVariantIds() {
    return cfg.gifts.strap.variants.map(function (v) { return Number(v.id); });
  }
  function strapVariantFor(title) {
    var hit = cfg.gifts.strap.variants.filter(function (v) {
      return String(v.title).toLowerCase() === String(title).toLowerCase();
    })[0];
    return hit ? Number(hit.id) : (cfg.gifts.strap.variants[0] ? Number(cfg.gifts.strap.variants[0].id) : 0);
  }
  function preferredStrapVariant() {
    var remembered = Number(storageGet());
    if (remembered && strapVariantIds().indexOf(remembered) !== -1) return remembered;
    return strapVariantFor(cfg.gifts.strap.defaultColor);
  }

  /* ---------- counting and planning ---------- */
  function analyse(cart) {
    var watches = 0;
    var thumbs = [];
    // cart.items is newest first; the bar shows watches in the order they were added.
    cart.items.slice().reverse().forEach(function (item) {
      if (!isWatch(item)) return;
      watches += item.quantity;
      for (var i = 0; i < item.quantity && thumbs.length < 4; i++) thumbs.push(item.image || '');
    });
    return { watches: watches, thumbs: thumbs };
  }
  function desired(watches) {
    var t2 = cfg.tiers[1].qty, t3 = cfg.tiers[2].qty;
    return {
      kit: cfg.gifts.kit.variantId ? watches : 0,
      strap: cfg.gifts.strap.variants.length && watches >= t2 ? 1 : 0,
      travel: cfg.gifts.travel.variantId && watches >= t3 ? 1 : 0
    };
  }
  // Returns { changes: [{key, quantity}], adds: [{id, quantity, role}] }.
  function plan(cart) {
    var want = desired(analyse(cart).watches);
    var changes = [], adds = [];
    var keep = { kit: null, strap: null, travel: null };
    var strapIds = strapVariantIds();
    var preferredStrap = preferredStrapVariant();
    var remembered = Number(storageGet());

    var giftProducts = [cfg.gifts.kit.productId, cfg.gifts.strap.productId, cfg.gifts.travel.productId].map(Number);
    var giftVariants = [Number(cfg.gifts.kit.variantId), Number(cfg.gifts.travel.variantId)].concat(strapIds);
    cart.items.forEach(function (item) {
      var role = giftRole(item);
      // A gift product without the hidden property (direct /cart/add link, old cart) is never
      // a valid line: the mechanic adds its own lines with the property.
      if (!role) {
        if (giftProducts.indexOf(Number(item.product_id)) !== -1 || giftVariants.indexOf(Number(item.variant_id)) !== -1) {
          changes.push({ key: item.key, quantity: 0 });
        }
        return;
      }
      var valid =
        (role === 'kit' && Number(item.variant_id) === Number(cfg.gifts.kit.variantId)) ||
        (role === 'travel' && Number(item.variant_id) === Number(cfg.gifts.travel.variantId)) ||
        (role === 'strap' && strapIds.indexOf(Number(item.variant_id)) !== -1);
      // Only a remembered colour forces a swap; otherwise any configured colour stays.
      if (role === 'strap' && valid && remembered && Number(item.variant_id) !== remembered) valid = false;
      if (!valid || keep[role] || want[role] === 0) {
        changes.push({ key: item.key, quantity: 0 });
        return;
      }
      keep[role] = item;
      if (item.quantity !== want[role]) changes.push({ key: item.key, quantity: want[role] });
    });

    if (want.kit && !keep.kit) adds.push({ id: Number(cfg.gifts.kit.variantId), quantity: want.kit, role: 'kit' });
    if (want.strap && !keep.strap) adds.push({ id: preferredStrap, quantity: 1, role: 'strap' });
    if (want.travel && !keep.travel) adds.push({ id: Number(cfg.gifts.travel.variantId), quantity: 1, role: 'travel' });
    return { changes: changes, adds: adds };
  }

  /* ---------- applying ---------- */
  // JSON templates wrap their sections as shopify-section-template--<id>__cart-items, static
  // sections as shopify-section-cart-drawer. The Section Rendering API wants the part after
  // "shopify-section-", so the ids are read from the DOM instead of being assumed.
  function sectionIdFor(name) {
    var el = document.getElementById('shopify-section-' + name) ||
      document.querySelector('[id^="shopify-section-"][id$="__' + name + '"]');
    return el ? el.id.replace(/^shopify-section-/, '') : '';
  }
  function pageSectionIds() {
    return ['cart-items', 'cart-footer'].map(sectionIdFor).filter(Boolean);
  }
  function sectionsForPage() {
    var list = [];
    if (document.querySelector('cart-drawer')) list.push(SECTIONS_DRAWER);
    return list.concat(pageSectionIds()).join(',');
  }
  function apply(ops) {
    var sections = sectionsForPage();
    var steps = [];
    ops.changes.forEach(function (c) { steps.push({ path: '/cart/change.js', body: { id: c.key, quantity: c.quantity } }); });
    ops.adds.forEach(function (a) {
      var properties = {};
      properties[cfg.propertyKey] = a.role;
      steps.push({ path: '/cart/add.js', body: { items: [{ id: a.id, quantity: a.quantity, properties: properties }] } });
    });
    if (!steps.length) return Promise.resolve(null);
    // Only the last write asks for the rendered sections, so one response refreshes the DOM.
    var last = steps[steps.length - 1];
    if (sections) { last.body.sections = sections; last.body.sections_url = window.location.pathname; }
    var chain = Promise.resolve(null);
    steps.forEach(function (step) {
      chain = chain.then(function () { return requestRetry(step.path, step.body); });
    });
    return chain;
  }
  function swapSections(sectionsHtml) {
    if (!sectionsHtml) return;
    state.muted = true;
    try {
      var parser = new DOMParser();
      if (sectionsHtml[SECTIONS_DRAWER]) {
        var fresh = parser.parseFromString(sectionsHtml[SECTIONS_DRAWER], 'text/html');
        var freshInner = fresh.querySelector('.drawer__inner');
        var inner = document.querySelector('cart-drawer .drawer__inner');
        var drawer = document.querySelector('cart-drawer');
        if (freshInner && inner) inner.innerHTML = freshInner.innerHTML;
        var freshDrawer = fresh.querySelector('cart-drawer');
        if (drawer && freshDrawer) drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      }
      pageSectionIds().forEach(function (id) {
        if (!sectionsHtml[id]) return;
        var wrap = document.getElementById('shopify-section-' + id);
        if (!wrap) return;
        var fresh = parser.parseFromString(sectionsHtml[id], 'text/html');
        var freshWrap = fresh.getElementById('shopify-section-' + id) || fresh.body;
        wrap.innerHTML = freshWrap.innerHTML;
      });
    } finally {
      // Let the mutation records of the swap flush before listening again.
      setTimeout(function () { state.muted = false; }, 0);
    }
  }
  // Free gift lines are not items for the customer: every visible count leaves them out.
  function visibleCount(cart) {
    return (cart.items || []).reduce(function (n, item) { return n + (giftRole(item) ? 0 : item.quantity); }, 0);
  }
  function paintCount(cart) {
    var count = visibleCount(cart);
    document.querySelectorAll('[data-cart-count], .cart-count-bubble span[aria-hidden]').forEach(function (el) {
      if (el.textContent !== String(count)) el.textContent = count;
    });
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.style.display = count > 0 ? '' : 'none';
    });
  }
  function announceCartChange(cart) {
    paintCount(cart);
    try { document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, source: 'tgm-rewards' } })); } catch (e) { /* old browsers */ }
  }

  /* ---------- bar ---------- */
  function fill(template, vars) {
    return String(template || '').replace(/\[(\w+)\]/g, function (m, key) {
      return vars[key] !== undefined ? vars[key] : m;
    });
  }
  function thumbUrl(url) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=96';
  }
  function progressFor(watches) {
    var t2 = cfg.tiers[1].qty, t3 = cfg.tiers[2].qty;
    if (watches >= t3) return 100;
    if (watches >= t2) return Math.round(50 + ((watches - t2) * 50) / Math.max(1, t3 - t2));
    if (watches >= 1) return Math.round(((watches - 1) * 50) / Math.max(1, t2 - 1));
    return 0;
  }
  function paintBar(cart) {
    var info = analyse(cart);
    var t2 = cfg.tiers[1], t3 = cfg.tiers[2];
    var tx = cfg.texts, accent, rest, more = 0;
    if (info.watches >= t3.qty) { accent = tx.m3a; rest = tx.m3r; }
    else if (info.watches >= t2.qty) { accent = tx.m2a; rest = tx.m2r; more = t3.qty - info.watches; }
    else if (info.watches >= 1) { accent = tx.m1a; rest = tx.m1r; more = t2.qty - info.watches; }
    else { accent = tx.m0a; rest = tx.m0r; }
    var vars = { more: more, watches: more === 1 ? 'watch' : 'watches', pct2: t2.pct, pct3: t3.pct };
    accent = fill(accent, vars);
    rest = fill(rest, vars);

    document.querySelectorAll('[data-tgm-rewards]').forEach(function (bar) {
      var pct = progressFor(info.watches);
      bar.style.setProperty('--tgm-rewards-progress', pct + '%');
      bar.style.setProperty('--tgm-rewards-progress-num', String(pct / 100));
      var a = bar.querySelector('[data-tgm-rewards-accent]');
      var r = bar.querySelector('[data-tgm-rewards-rest]');
      if (a && a.textContent !== accent) a.textContent = accent;
      if (r && r.textContent !== rest) r.textContent = rest;
      bar.querySelectorAll('[data-tgm-step]').forEach(function (step) {
        var qty = Number(step.getAttribute('data-qty'));
        step.classList.toggle('is-reached', info.watches >= qty);
        var box = step.querySelector('[data-tgm-rewards-box]');
        if (!box) return;
        var slots = Math.min(qty, 4);
        var html = '';
        for (var i = 0; i < slots; i++) {
          if (i < info.watches && info.thumbs[i]) {
            html += '<span class="tgm-rewards__slot"><img src="' + thumbUrl(info.thumbs[i]) + '" alt="" width="22" height="22" loading="lazy"></span>';
          } else {
            html += '<span class="tgm-rewards__slot tgm-rewards__slot--empty">?</span>';
          }
        }
        if (box.innerHTML !== html) box.innerHTML = html;
      });
    });
  }
  // On the cart page the rows are table rows, so CSS order cannot move them; do it here.
  function reorderGiftRows() {
    document.querySelectorAll('.tgm-gift-line').forEach(function (row) {
      var parent = row.parentNode;
      if (parent && parent.lastElementChild !== row) parent.appendChild(row);
    });
    ['kit', 'strap', 'travel'].forEach(function (role) {
      document.querySelectorAll('.tgm-gift-line[data-tgm-gift="' + role + '"]').forEach(function (row) {
        row.parentNode.appendChild(row);
      });
    });
  }

  /* ---------- sync ---------- */
  function sync(reason) {
    if (!cfg) return Promise.resolve();
    if (state.syncing) { state.pending = true; return Promise.resolve(); }
    state.syncing = true;
    document.documentElement.classList.add('tgm-rewards-busy');
    log('sync', reason);
    return request('/cart.js')
      .then(function (cart) {
        var ops = plan(cart);
        if (!ops.changes.length && !ops.adds.length) {
          paintBar(cart);
          paintCount(cart);
          reorderGiftRows();
          return null;
        }
        log('ops', ops);
        return apply(ops).then(function (lastResponse) {
          return request('/cart.js').then(function (fresh) {
            swapSections(lastResponse && lastResponse.sections);
            paintBar(fresh);
            reorderGiftRows();
            announceCartChange(fresh);
          });
        });
      })
      .catch(function (err) { log('sync failed', err); })
      .then(function () {
        state.syncing = false;
        if (!state.ready) { state.ready = true; document.documentElement.classList.add('tgm-rewards-ready'); }
        document.documentElement.classList.remove('tgm-rewards-busy');
        if (state.pending) { state.pending = false; return sync('pending'); }
      });
  }
  function scheduleSync(reason) {
    clearTimeout(state.timer);
    state.timer = setTimeout(function () { sync(reason); }, DEBOUNCE_MS);
  }

  // Re-render the cart sections without writing to the cart (used after a paid add from the
  // Pair it with card, so the card hides itself and the bar repaints).
  function refresh(reason) {
    var sections = sectionsForPage();
    log('refresh', reason);
    var paint = function () {
      return request('/cart.js').then(function (cart) { paintBar(cart); reorderGiftRows(); announceCartChange(cart); });
    };
    if (!sections) return paint();
    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(sections), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (html) { swapSections(html); })
      .catch(function (err) { log('refresh failed', err); })
      .then(paint);
  }
  function addPair(button) {
    var id = Number(button.getAttribute('data-tgm-pair-add'));
    if (!id || button.disabled) return;
    button.disabled = true;
    var sections = sectionsForPage();
    var body = { items: [{ id: id, quantity: 1 }] };
    if (sections) { body.sections = sections; body.sections_url = window.location.pathname; }
    requestRetry('/cart/add.js', body)
      .then(function (res) {
        swapSections(res && res.sections);
        return request('/cart.js').then(function (cart) { paintBar(cart); reorderGiftRows(); announceCartChange(cart); });
      })
      .catch(function (err) { log('pair add failed', err); button.disabled = false; });
  }


  /* ---------- discount code field (04-10-2026) ---------- */
  // Writes the code with /cart/update.js {discount}. A code that does not apply is rolled back to
  // the codes that were already there, so a typo never wipes a valid one.
  function codeBox(el) { return el.closest('[data-tgm-code]'); }
  function codeMessage(box, text, isError) {
    var msg = box.querySelector('[data-tgm-code-msg]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.hidden = !text;
    msg.classList.toggle('is-error', !!isError);
  }
  function writeCodes(codes, sections) {
    var body = { discount: codes.join(',') };
    if (sections) { body.sections = sections; body.sections_url = window.location.pathname; }
    return request('/cart/update.js', body);
  }
  function applyCode(box) {
    var input = box.querySelector('[data-tgm-code-input]');
    var button = box.querySelector('[data-tgm-code-apply]');
    var code = (input && input.value || '').trim();
    if (!code || (button && button.disabled)) return;
    var prior = (box.getAttribute('data-applied') || '').split(',').filter(Boolean);
    var invalid = box.getAttribute('data-invalid') || "This code isn't valid for your cart.";
    if (button) button.disabled = true;
    codeMessage(box, '', false);
    writeCodes([code], sectionsForPage()).then(function (cart) {
      var hit = (cart.discount_codes || []).filter(function (d) { return d.code.toLowerCase() === code.toLowerCase(); })[0];
      if (!hit || !hit.applicable) {
        codeMessage(box, invalid, true);
        if (button) button.disabled = false;
        return writeCodes(prior);
      }
      swapSections(cart.sections);
      return request('/cart.js').then(function (c) { paintBar(c); reorderGiftRows(); announceCartChange(c); });
    }).catch(function (err) {
      log('code failed', err);
      codeMessage(box, invalid, true);
      if (button) button.disabled = false;
    });
  }
  function removeCode(box) {
    writeCodes([], sectionsForPage()).then(function (cart) {
      swapSections(cart.sections);
      return request('/cart.js').then(function (c) { paintBar(c); reorderGiftRows(); announceCartChange(c); });
    }).catch(function (err) { log('code remove failed', err); });
  }
  function wireCodeField() {
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-tgm-code-toggle],[data-tgm-code-apply],[data-tgm-code-remove]');
      if (!t) return;
      var box = codeBox(t);
      if (!box) return;
      e.preventDefault();
      if (t.hasAttribute('data-tgm-code-toggle')) {
        var row = box.querySelector('.tgm-cart-code__row');
        var open = row.hidden;
        row.hidden = !open;
        t.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) { var inp = row.querySelector('input'); if (inp) inp.focus(); }
      } else if (t.hasAttribute('data-tgm-code-apply')) applyCode(box);
      else removeCode(box);
    });
    // Enter inside the field must not submit the cart form (that would go to checkout).
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || !e.target.matches || !e.target.matches('[data-tgm-code-input]')) return;
      e.preventDefault();
      e.stopPropagation();
      var box = codeBox(e.target);
      if (box) applyCode(box);
    }, true);
  }

  /* ---------- wiring ---------- */
  function observe() {
    var observer = new MutationObserver(function (records) {
      if (state.muted) return;
      var relevant = records.some(function (rec) {
        var node = rec.target.nodeType === 1 ? rec.target : rec.target.parentElement;
        if (!node) return false;
        if (node.closest && node.closest('tgm-cart-rewards, tgm-cart-timer, countdown-timer, .cart-timer, .tgm-cart-delivery')) return false;
        if (rec.type === 'attributes' && rec.target.tagName === 'CART-DRAWER') {
          return rec.target.classList.contains('active') && !rec.oldValue.split(' ').includes('active');
        }
        return true;
      });
      if (relevant) scheduleSync('mutation');
    });
    var drawer = document.querySelector('cart-drawer');
    if (drawer) observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'], attributeOldValue: true });
    var pageId = sectionIdFor('cart-items');
    var page = pageId ? document.getElementById('shopify-section-' + pageId) : null;
    if (page) observer.observe(page, { childList: true, subtree: true });
  }
  function wireEvents() {
    ['cart:update', 'cart:refresh', 'cart:item-added', 'cartQuantityUpdated'].forEach(function (name) {
      document.addEventListener(name, function (e) {
        if (e && e.detail && e.detail.source === 'tgm-rewards') return;
        scheduleSync(name);
      });
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      try { subscribe(PUB_SUB_EVENTS.cartUpdate, function () { scheduleSync('pubsub'); }); } catch (e) { /* ignore */ }
    }

    // Strap colour: remember the choice, then let the planner swap the line.
    document.addEventListener('click', function (e) {
      var swatch = e.target.closest && e.target.closest('[data-tgm-swatch]');
      if (swatch) {
        e.preventDefault();
        storageSet(swatch.getAttribute('data-tgm-swatch'));
        swatch.parentNode.querySelectorAll('[data-tgm-swatch]').forEach(function (b) {
          b.classList.toggle('is-selected', b === swatch);
          b.setAttribute('aria-pressed', b === swatch ? 'true' : 'false');
        });
        sync('swatch');
        return;
      }
      var pairBtn = e.target.closest && e.target.closest('[data-tgm-pair-add]');
      if (pairBtn) { e.preventDefault(); addPair(pairBtn); return; }
      var info = e.target.closest && e.target.closest('[data-tgm-rewards-info]');
      document.querySelectorAll('[data-tgm-rewards-info].is-open').forEach(function (b) { if (b !== info) b.classList.remove('is-open'); });
      if (info) info.classList.toggle('is-open');
    });

    // Checkout: sync first, then go. Capture phase so it runs before the theme's own handlers.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || (form.id !== 'CartDrawer-Form' && form.id !== 'cart')) return;
      var submitter = e.submitter;
      var isCheckout = (submitter && submitter.name === 'checkout') || (!submitter && form.querySelector('[name="checkout"]'));
      if (!isCheckout) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (form.dataset.tgmRewardsGoing) return;
      form.dataset.tgmRewardsGoing = '1';
      var go = function () { window.location.href = ROOT.replace(/\/$/, '') + '/checkout'; };
      // Never block the sale: a sync that fails or hangs still goes to checkout.
      var guard = setTimeout(go, 6000);
      sync('checkout').then(function () { clearTimeout(guard); go(); }, function () { clearTimeout(guard); go(); });
    }, true);
  }

  function init() {
    cfg = readConfig();
    if (!cfg || !cfg.enabled) return;
    observe();
    wireEvents();
    wireCodeField();
    sync('load');
  }

  window.TGMRewards = { sync: sync, refresh: refresh, plan: plan, analyse: analyse, visibleCount: visibleCount, get config() { return cfg; } };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
